import React from "react";
import { showToast } from "../../../../utils/Helper";

// Renders the alert event history as a vertical timeline with day separators.
// Cards float on the right of the connector line; avatars overlap the line.

const wrapperStyle = {
  width: "100%",
  background: "#ffffff",
  border: "1px solid #e3e8ec",
  borderRadius: "6px",
  padding: "16px 18px",
};

const titleStyle = {
  fontSize: "12px",
  fontWeight: 600,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: "#455a64",
  marginBottom: "16px",
};

const timelineWrapperStyle = {
  position: "relative",
  paddingLeft: "56px",
};

const lineStyle = {
  position: "absolute",
  left: "24px",
  top: "10px",
  bottom: "10px",
  width: "2px",
  background: "#e3e8ec",
  zIndex: 0,
};

const dayLabelStyle = {
  display: "inline-block",
  position: "relative",
  background: "#ffffff",
  padding: "2px 10px",
  marginLeft: "-44px",
  marginTop: "6px",
  marginBottom: "10px",
  fontSize: "10px",
  fontWeight: 600,
  letterSpacing: "0.6px",
  textTransform: "uppercase",
  color: "#90a4ae",
  borderRadius: "10px",
  border: "1px solid #e3e8ec",
  zIndex: 1,
};

const cardStyle = {
  position: "relative",
  background: "#ffffff",
  border: "1px solid #e3e8ec",
  borderRadius: "8px",
  padding: "12px 14px",
  marginBottom: "10px",
  boxShadow: "0 1px 0 rgba(38, 50, 56, 0.02)",
  transition: "box-shadow 0.15s ease, transform 0.15s ease",
  zIndex: 1,
};

const cardHoverStyle = {
  boxShadow: "0 2px 8px rgba(38, 50, 56, 0.08)",
};

const avatarBaseStyle = {
  position: "absolute",
  left: "-48px",
  top: "10px",
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  color: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "12px",
  fontWeight: 600,
  letterSpacing: "0.4px",
  boxShadow: "0 0 0 3px #ffffff",
  zIndex: 2,
};

const headerRowStyle = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "8px",
  marginBottom: "6px",
};

const userStyle = {
  fontWeight: 600,
  color: "#263238",
  fontSize: "13px",
};

const relativeTimeStyle = {
  color: "#90a4ae",
  fontSize: "12px",
  cursor: "default",
};

const messageStyle = {
  color: "#37474f",
  fontSize: "13px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  lineHeight: 1.5,
};

const filesRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "6px",
  marginTop: "8px",
};

const fileChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  padding: "3px 9px",
  background: "#eceff1",
  color: "#455a64",
  borderRadius: "12px",
  fontSize: "12px",
  cursor: "pointer",
  border: "1px solid transparent",
  transition: "border-color 0.12s ease, background 0.12s ease",
};

const STATUS_BADGE_STYLE = {
  padding: "2px 8px",
  borderRadius: "10px",
  fontSize: "10px",
  fontWeight: 600,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: "#fff",
};

const STATUS_PALETTE = {
  SUBMITTED: "#1c84c6",
  "REQUESTED INFO": "#f8ac59",
  APPROVED: "#1ab394",
  REJECTED: "#e74c3c",
  ESCALATED: "#9b59b6",
  NOTE: "#90a4ae",
};

// Deterministic colour per user. Cap at the palette length.
const AVATAR_PALETTE = [
  "#455a64", // slate
  "#1c84c6", // blue
  "#1ab394", // teal
  "#f8ac59", // amber
  "#e74c3c", // rose
  "#9b59b6", // plum
  "#607d8b", // gray-slate
];

const hashString = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};

const getAvatarColor = (name) => {
  if (!name) return AVATAR_PALETTE[0];
  return AVATAR_PALETTE[hashString(String(name)) % AVATAR_PALETTE.length];
};

const getInitials = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const resolveStatus = (event) => {
  if (!event) return "NOTE";
  const raw = event.kind || event.type || event.action || event.status || event.eventType;
  if (raw) return String(raw).toUpperCase();
  return "NOTE";
};

// Use UTC for grouping/display to match the rest of the codebase (PostItem
// renders with `timeZone="UTC"`). Otherwise events near midnight get pulled
// into the previous/next local day.
const utcKey = (d) =>
  `${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`;

const dayLabel = (date) =>
  date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

const formatRelative = (date) => {
  if (!date) return "";
  const dayPart = date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
  // Backend sends naked timestamps (no Z), so they're parsed as local time.
  // Render the hour in local time to match what was originally recorded.
  const timePart = date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${dayPart} · ${timePart}`;
};

const StatusBadge = ({ value }) => (
  <span
    style={{
      ...STATUS_BADGE_STYLE,
      background: STATUS_PALETTE[value] || STATUS_PALETTE.NOTE,
    }}
  >
    {value}
  </span>
);

// Chip de archivo: click = descargar (comportamiento previo). Si llegan
// `fileTypes` + `onSendFileType`, además muestra un ícono "tag" para elegir un
// tipo de archivo y enviarlo (SetComplianceEventFiles). Si esos props no se
// pasan, se comporta exactamente como antes (solo descarga).
const FileChip = ({ file, downloadFile, fileTypes, onSendFileType }) => {
  const [open, setOpen] = React.useState(false);
  const [selectedType, setSelectedType] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [tagged, setTagged] = React.useState(false);
  const canTag = !!onSendFileType && Array.isArray(fileTypes) && fileTypes.length > 0;

  const handleDownload = () => {
    if (!downloadFile) return;
    downloadFile(
      { eventId: file.eventId, eventHistoryId: file.eventHistoryId, eventHistoryFileId: file.id },
      (r) => {
        if (r && r.url) window.open(r.url);
      }
    );
  };

  const handleSend = () => {
    if (!selectedType || sending) return;
    setSending(true);
    onSendFileType(
      {
        eventId: file.eventId,
        eventHistoryId: file.eventHistoryId,
        eventHistoryFileId: file.id,
        subTypeId: selectedType,
      },
      (response, exception) => {
        setSending(false);
        if (exception) {
          showToast("Could not add the file to the Archives tab. Please try again.", true);
          return;
        }
        setTagged(true);
        setOpen(false);
        showToast("File added to the Archives tab successfully.", false);
      }
    );
  };

  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 4 }}>
      <span
        style={fileChipStyle}
        onClick={handleDownload}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "#cfd8dc";
          e.currentTarget.style.background = "#e3e8ec";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "transparent";
          e.currentTarget.style.background = "#eceff1";
        }}
      >
        <i className="fa fa-paperclip" aria-hidden="true" />
        {file.name}
        {tagged ? (
          <i className="fa fa-check" aria-hidden="true" style={{ marginLeft: 8, color: "#2e7d32" }} title="File type sent" />
        ) : canTag ? (
          <i
            className="fa fa-tag"
            aria-hidden="true"
            style={{ marginLeft: 8, color: "#607d8b", cursor: "pointer" }}
            title="Set file type"
            onClick={(e) => {
              e.stopPropagation();
              setOpen((o) => !o);
            }}
          />
        ) : null}
      </span>
      {open && canTag && (
        <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          <select
            value={selectedType}
            disabled={sending}
            onChange={(e) => setSelectedType(e.target.value)}
            style={{ fontSize: 12, padding: "2px 6px", borderRadius: 4, border: "1px solid #cfd8dc" }}
          >
            <option value="">File type</option>
            {fileTypes.map((t) => (
              <option key={t.ArchiveSubTypeId} value={t.ArchiveSubTypeId}>
                {t.ArchiveSubTypeDescription}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!selectedType || sending}
            onClick={handleSend}
            className="btn btn-primary btn-sm"
            style={{ fontSize: 12, padding: "2px 10px" }}
          >
            {sending ? "Sending…" : "Send"}
          </button>
        </span>
      )}
    </span>
  );
};

const TimelineItem = ({ event, downloadFile, fileTypes, onSendFileType }) => {
  const [hover, setHover] = React.useState(false);
  const status = resolveStatus(event);
  const user = event.user || event.userName || "Unknown";
  const date = event.date ? new Date(event.date) : null;

  const exactDate = date
    ? `${date.toLocaleDateString(undefined, { timeZone: "UTC" })} ${date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}`
    : "";

  return (
    <div
      style={{ ...cardStyle, ...(hover ? cardHoverStyle : null) }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div style={{ ...avatarBaseStyle, background: getAvatarColor(user) }}>{getInitials(user)}</div>
      <div style={headerRowStyle}>
        <span style={userStyle}>{user}</span>
        <StatusBadge value={status} />
        {date && (
          <span style={relativeTimeStyle} title={exactDate}>
            {formatRelative(date)}
          </span>
        )}
      </div>
      {event.message && <div style={messageStyle}>{event.message}</div>}
      {Array.isArray(event.files) && event.files.length > 0 && (
        <div style={filesRowStyle}>
          {event.files.map((f) => (
            <FileChip
              key={`f-${f.id}`}
              file={f}
              downloadFile={downloadFile}
              fileTypes={fileTypes}
              onSendFileType={onSendFileType}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FollowUpTimeline = ({ events, downloadFile, fileTypesFetch, onSendFileType, personType }) => {
  // Feature opcional "set file type": solo se activa si llegan ambos hooks.
  const [fileTypes, setFileTypes] = React.useState(null);
  React.useEffect(() => {
    if (!fileTypesFetch || !onSendFileType || fileTypes !== null) return;
    fileTypesFetch({}, (r) => {
      const list = r && r.SDTGetArchivesTypes && r.SDTGetArchivesTypes.ArchivesTypes;
      if (!Array.isArray(list)) {
        setFileTypes([]);
        return;
      }
      // Mismo criterio que el flujo de alerta (NewPost/Files): índice por tipo de persona.
      const idx = personType === "Physical" ? 2 : personType === "All" ? 1 : 0;
      const node = list[idx];
      setFileTypes((node && node.ArchivesSubTypes) || []);
    });
  }, [fileTypesFetch, onSendFileType, personType, fileTypes]);

  const sorted =
    Array.isArray(events) && events.length > 0
      ? [...events].sort(
          (a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime()
        )
      : [];

  // Group consecutive events by UTC day, preserving order.
  const groups = [];
  let currentKey = null;
  sorted.forEach((ev) => {
    const d = ev.date ? new Date(ev.date) : null;
    const key = d ? utcKey(d) : "unknown";
    if (key !== currentKey) {
      groups.push({ key, date: d, items: [] });
      currentKey = key;
    }
    groups[groups.length - 1].items.push(ev);
  });

  return (
    <div style={wrapperStyle}>
      <div style={titleStyle}>Follow-up</div>
      {sorted.length === 0 ? (
        <div style={{ color: "#90a4ae", fontSize: 13, padding: "12px 0" }}>
          No activity yet.
        </div>
      ) : (
        <div style={timelineWrapperStyle}>
          {sorted.length > 1 && <div style={lineStyle} />}
          {groups.map((group, gi) => (
            <React.Fragment key={`g-${gi}-${group.key}`}>
              {group.date && <div style={dayLabelStyle}>{dayLabel(group.date)}</div>}
              {group.items.map((event, i) => (
                <TimelineItem
                  key={event.id || `ev-${gi}-${i}`}
                  event={event}
                  downloadFile={downloadFile}
                  fileTypes={fileTypes}
                  onSendFileType={onSendFileType}
                />
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default FollowUpTimeline;
